import { DateStr, DateTimeRange, DateTimeStr, Milliseconds, Minutes } from '../types/date';
import { User } from './users';

/** Minimal station data embedded in legacy audit report responses. */
interface AuditReportStation {
  readonly id: string;
  readonly name: string;
}

export interface AuditAudio {
  readonly id: string;
  readonly customId: string;
  readonly title: string;
  readonly duration: Milliseconds;
  readonly filename: string;
  readonly createdAt: DateTimeStr;
  readonly updatedAt: DateTimeStr;
  readonly stationId: string | null;
  readonly stationsGroupId: string | null;
  readonly customData: Record<string, unknown> | null;
  readonly transcription?: {
    readonly content: string;
  };
}

export interface AuditSchedule {
  readonly id: string;
  readonly stationId: string;
  readonly datetime: DateTimeStr;
  readonly tolerance: Minutes | null;
  readonly toleranceBefore: Minutes | null;
  readonly toleranceConfig: ScheduleToleranceConfig;
  readonly customId: string;
  readonly audio?: AuditAudio | null;
  readonly matches?: null | readonly MinimumAuditMatch[];
  readonly mentionMatch?: AuditLiveMention | null;
  readonly campaign: string | null;
  readonly advertiser: string | null;
  readonly version: string | null;
  readonly custom: Record<string, unknown> | null;
  readonly order?: string | null;
  readonly type?: string | null;
}

export enum ScheduleToleranceSource {
  ENTRY = 'ENTRY',
  INHERITED_FROM_CLIENT_CONFIG = 'INHERITED_FROM_CLIENT_CONFIG',
}

export interface ScheduleToleranceConfig {
  before: {
    value: Minutes | null;
    source: ScheduleToleranceSource;
  };
  after: {
    value: Minutes | null;
    source: ScheduleToleranceSource;
  };
}

export interface AuditMatch {
  readonly type: AuditMatchType;
  readonly datetime: DateTimeStr;
  readonly playedDuration: Milliseconds;
  readonly score: number; // 0 to 100
  readonly matchedSchedule: null | AuditSchedule;
  readonly matchedScheduleDiff: null | Minutes;
  readonly matchedScheduleConfirmationType: null | 'DEFAULT' | 'OFF_TOLERANCE';
  readonly audio?: AuditAudioInMatch;
  readonly music?: {
    title: string;
    artist: string;
  };
  readonly source: 'MANUAL' | 'PROVIDER';
}

// TODO: FIX THIS
export type AuditAudioInMatch = Omit<AuditAudio, 'id'> & {
  customId: string;
};

// TODO: FIX THIS
export type AuditAudioAlt = AuditAudio & {
  customId: string;
};

export enum AuditMatchType {
  MUSIC = 'MUSIC',
  CUSTOM = 'CUSTOM',
}

export interface MinimumAuditMatch {
  readonly type: AuditMatchType;
  readonly datetime: DateTimeStr;
  readonly playedDuration: Milliseconds;
  readonly score: number; // 0 to 100
  readonly matchedSchedule: null | AuditSchedule;
  readonly matchedScheduleDiff: null | Minutes;
  readonly matchedScheduleConfirmationType: null | 'DEFAULT' | 'OFF_TOLERANCE';
  readonly matchedDiff: Minutes;
}

/**
 * Reports
 */

export interface Report<Data = object> {
  readonly id: string;
  readonly clientId: string;
  readonly name: string;
  readonly type: string;
  readonly data: Data;
  readonly createdAt: DateTimeStr;
  readonly createdById: User['id'] | null;
  readonly createdBy?: User;
}

/**
 * Station reports
 */

// Audios that have same MD5 (= same content)
export interface EqualMd5AudiosReportDto {
  config: {
    stationId: string;
    range: DateTimeRange;
  };
  total: number;
  items: {
    md5: string;
    audios: readonly AuditAudio[];
  }[];
}

// Audios created in audioRange that have no matches in
// the range matchRange
// If returns the audio with matches and the same md5 if it exists
export interface NoMatchesReport {
  config: {
    stationId: string;
    audioRange: DateTimeRange;
    matchRange: DateTimeRange;
  };
  total: number;
  items: {
    audio: AuditAudio;
    sameMd5MatchedAudio: AuditAudio | null;
  }[];
}

// Missing audios for schedules for the given range
export interface MissingAudiosReportDto {
  config: {
    stationsIds: string[];
    from: DateTimeStr;
    to: DateTimeStr;
    groupBy?: 'none' | 'custom_id' | 'custom_id,station_id';
  };
  total: number;
  items: MissingAudiosReportItem[];
}

export type MissingAudiosReportItem = AuditSchedule;

// Schedules grouped by custom ID with the related audio
// the range matchRange
// If returns the audio with matches and the same md5 if it exists
export interface ScheduleStatusReport {
  config: {
    stationId: string;
    range: DateTimeRange;
    customId: string | null;
  };
  total: number;
  items: ScheduleStatusReportItem[];
}

export interface ScheduleStatusReportItem {
  customId: string;
  audio: AuditAudio | null;
  status: {
    matchesCount: number;
    matchesWithScheduleCount: number;
    matchesWithScheduleOffToleranceCount: number;
    schedulesCount: number;
  };
  schedules: AuditSchedule[];
}

export interface GlobalNoMatchesReportDto {
  config: {
    audioRange: DateTimeRange;
    matchRange: DateTimeRange;
  };
  total: number;
  items: GlobalNoMatchesReportItem[];
}

export type GlobalNoMatchesReportItem = {
  audio: AuditAudio & {
    station?: AuditReportStation;
    stationsGroup?: AuditReportStation;
  };
};

export interface GlobalSchedulesNoMatchesReport {
  config: {
    clientId: string;
    stationsIds: string[];
    from: DateTimeStr;
    to: DateTimeStr;
    includeMissingAudio?: boolean;
  };
  total: number;
  items: GlobalSchedulesNoMatchesReportItem[];
}

export type GlobalSchedulesNoMatchesReportItem = {
  schedule: AuditSchedule & {
    station: AuditReportStation;
    audio?: AuditAudio;
  };
};

export enum AuditLiveMentionConfidence {
  AUTO = 'AUTO',
  MANUAL = 'MANUAL',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

export interface AuditLiveMention {
  readonly id: string;
  readonly stationId: string;
  readonly datetime: DateTimeStr;
  readonly name: string;
  readonly type: string | null;
  readonly description: string | null;
  readonly author: string | null;
  readonly customData: Record<string, unknown> | null;
  readonly confidence: AuditLiveMentionConfidence;
  /** Detected mention length in seconds; null when unknown. */
  readonly duration: number | null;
  readonly confirmedSchedule?: AuditSchedule | null;
}

export interface CreateScheduleDtoV2 {
  readonly datetime: DateTimeStr;
  readonly customId: string;
  readonly tolerance?: Minutes | null;
  readonly toleranceBefore?: Minutes | null;
  readonly stationId?: string;
  readonly campaign?: string;
  readonly advertiser?: string;
  readonly version?: string;
  readonly custom?: Record<string, unknown>;
  readonly parentId?: AuditSchedule['id'] | null;
}

export interface CreatePntDto {
  date: string;
  customId: string;
  title: string;
  count: number;
  dirtyText: string;
}

export type CreatePntsDto = CreatePntDto[];

export interface PntStationStatusReportDto {
  results: PntStationStatusItemDto[];
}

export interface PntStationStatusItemDto {
  customId: string;
  schedule: GetPntScheduleDto;
  matches: GetPntMatchDto[];
}

export interface GetPntScheduleDto {
  id: string;
  customId: string;
  date: string;
  original: string;
  name: string;
  count: number;
}

export interface GetPntMatchDto {
  stationId: string;
  datetime: DateTimeStr;
  name: string;
  type: string | null;
  description: string | null;
  author: string | null;
  customData: object | null;
  confidence: AuditLiveMentionConfidence;
  confirmedSchedule?: AuditSchedule | null;
}

/**
 * Daily mention quota (date-schedule) as returned by the PNT schedule-status v2 report.
 * Includes advertiser and the mention script (`original`).
 */
export interface PntDateScheduleDtoV2 {
  readonly id: string;
  readonly customId: string;
  readonly date: DateStr;
  readonly original: string;
  readonly name: string;
  readonly advertiser: string | null;
  readonly count: number;
}

/** Live-mention detection in the PNT schedule-status v2 report (no schedule confirmation nested). */
export interface PntStationStatusMatchDtoV2 {
  readonly stationId: string;
  readonly datetime: DateTimeStr;
  readonly name: string;
  readonly type: string | null;
  readonly description: string | null;
  readonly author: string | null;
  readonly confidence: AuditLiveMentionConfidence;
}

/**
 * Coverage counts for a grouped custom ID in the PNT schedule-status v2 report.
 * Same shape as the spots schedule-status report.
 */
export interface PntStationStatusCoverageDtoV2 {
  readonly matchesCount: number;
  readonly matchesWithScheduleCount: number;
  readonly matchesWithScheduleOffToleranceCount: number;
  readonly schedulesCount: number;
}

/**
 * PNT schedule-status v2 report: date-schedule plus per-slot schedules (with mentionMatch)
 * and the day's detections.
 */
export interface PntStationStatusReportDtoV2 {
  readonly config: {
    readonly stationId: string;
    readonly date: DateStr;
    readonly customIds: string[] | null;
  };
  readonly total: number;
  readonly items: PntStationStatusItemDtoV2[];
}

export interface PntStationStatusItemDtoV2 {
  readonly customId: string;
  readonly dateSchedule: PntDateScheduleDtoV2 | null;
  readonly status: PntStationStatusCoverageDtoV2;
  readonly schedules: AuditSchedule[];
  readonly matches: PntStationStatusMatchDtoV2[];
}

export interface EvidenceExportPathFormat {
  id: string;
  label: string;
  pattern: string;
  default?: boolean;
}

export interface GetAuditConfigDto {
  excludedCustomIds: string[];
  evidenceExport?: {
    pathFormats?: EvidenceExportPathFormat[];
  };
  runAsLogConfirmation?: {
    system?: 'bcr' | 'ltx' | 'avg';
  };
}

export interface UpdateAuditConfigDto {
  excludedCustomIds: string[];
}

export enum OperationStatus {
  NONE = 'NONE',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export type GipReportParametersForOrderIdResult = {
  stationsIds: string[];
  advertisers: string[];
  campaigns: string[];
  versions: string[];
  audiosInfo: {
    audioId: string | null;
    customId: string;
  }[];
  schedulesRange: {
    from: string | null;
    to: string | null;
  };
};
